import { useRef } from "react";
import { Head } from "@-/com.npmjs.expo-router/head";
import {
  APP_STORE_APP_ID,
  WebsiteFooter,
  WebsitePage,
} from "@-/dev.improve.haniverse.components.website";
import { LandingCurriculumSection } from "#landing-curriculum-section";
import { LandingDownloadSection } from "#landing-download-section";
import { LandingFaqSection } from "#landing-faq-section";
import { LandingHero } from "#landing-hero";
import { LandingMnemonicSection } from "#landing-mnemonic-section";
import { LandingPinyinSection } from "#landing-pinyin-section";
import { LandingScreenTour } from "#landing-screen-tour";
import { LandingStickyDownloadBar } from "#landing-sticky-download-bar";

const LANDING_TITLE = "Haniverse — Learn Chinese characters with stories that stick";
const LANDING_DESCRIPTION =
  "The Chinese character app for iPhone and Android. Every character comes with a memorable story, and every lesson, pinyin included, is built only from what you’ve already learned.";

/**
 * Web-only public marketing page. Expo Router adapters render this package at both `/` and
 * `/landing`; keeping the page outside the `(nav)` route group avoids initializing app databases.
 *
 * The page is built around Haniverse's two promises, each shown with real curriculum content: a
 * story for every character, and lessons (pinyin included) that only use what came before.
 *
 * The page sells the native app: every call to action downloads it, and the web app is reachable
 * only through a quiet "Log in" link for existing accounts.
 */
export function IndexPage() {
  const heroCallToActionRef = useRef<HTMLDivElement>(null);

  return (
    <WebsitePage description={LANDING_DESCRIPTION} title={LANDING_TITLE}>
      <Head>
        {/* Safari's Smart App Banner: an iPhone visitor gets a native "Open / Get" bar on top. */}
        <meta content={`app-id=${APP_STORE_APP_ID}`} name="apple-itunes-app" />
      </Head>
      <LandingHero callToActionRef={heroCallToActionRef} />
      <main>
        <LandingMnemonicSection />
        <LandingCurriculumSection />
        <LandingPinyinSection />
        <LandingScreenTour />
        <LandingFaqSection />
        <LandingDownloadSection />
      </main>
      {/* Phones keep the footer's links clear of the sticky download bar. */}
      <WebsiteFooter className="pb-28 sm:pb-12" />
      <LandingStickyDownloadBar heroCallToActionRef={heroCallToActionRef} />
    </WebsitePage>
  );
}
